import React, { useEffect, useMemo, useRef, useState } from 'react';
import StockRecipeChoices, { stockChoiceValid } from './StockRecipeChoices';
import OrgasmeatEmoji, { getOrgasmeatDisplayName } from './OrgasmeatEmoji';
import OrgasmeatActionIcon from './OrgasmeatActionIcon';
import './OrgasmeatMenu.css';

const euro = cents => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
const cents = value => value !== null && value !== '' && Number.isFinite(Number(value)) ? Math.round(Number(value) * 100) : null;
const surcharge = product => cents(product?.MenuProducts?.additionalPrice ?? 0);
const sameId = (first, second) => String(first) === String(second);
const recipeValid = product => { try { return stockChoiceValid(product); } catch (_) { return false; } };

function MenuProductImage({ product }) {
  const source = product.thumbnailImage || product.image;
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [source]);
  return source && !failed
    ? <img src={source} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
    : <span className="orgasmeat-menu-photo-placeholder" aria-hidden="true">{Array.from(product.name?.trim() || '')[0]?.toUpperCase() || '✦'}</span>;
}

function summarize(menu, sections, selections) {
  let total = cents(menu?.price);
  const steps = sections.map(section => {
    const required = Math.max(1, Number(section.requiredQuantity || section.MenuSections?.requiredQuantity || 1));
    const selected = Array.isArray(selections[section.id]) ? selections[section.id] : [];
    const products = Array.isArray(section.products) ? section.products : [];
    const available = selected.every(product => products.some(candidate => sameId(candidate.id, product.id)));
    const valid = Number.isInteger(required) && selected.length === required
      && available && selected.every(product => recipeValid(product) && surcharge(product) !== null);
    const missing = Math.max(0, required - selected.length);
    const prices = products.map(surcharge).filter(value => value !== null);
    const minimum = prices.length ? Math.min(...prices) : null;
    const selectedPrices = selected.map(surcharge);
    if (!available || selectedPrices.some(value => value === null) || (missing > 0 && minimum === null) || !Number.isInteger(required)) total = null;
    if (total !== null) total += selectedPrices.reduce((sum, value) => sum + value, 0) + (missing ? minimum * missing : 0);
    return { ...section, required, selected, products, valid, missing };
  });
  return { steps, total, complete: steps.length > 0 && steps.every(step => step.valid) && total !== null && total >= 0 };
}

function stepTitle(step) {
  if (!step) return 'Menu indisponible';
  const name = String(step.name).toLowerCase();
  const label = ['à côté', 'side', 'sides', 'accompagnement', 'accompagnements'].includes(name) ? ['ton accompagnement', 'accompagnements']
    : ['smash burgers', 'burger', 'burgers'].includes(name) ? ['ton burger', 'burgers']
    : ['boisson', 'boissons'].includes(name) ? ['ta boisson', 'boissons']
    : ['happy ending', 'dessert', 'desserts'].includes(name) ? ['ton dessert', 'desserts'] : null;
  return label ? (step.required === 1 ? `Choisis ${label[0]}` : `Choisis tes ${step.required} ${label[1]}`) : `Choisis ${step.required} ${name}`;
}

function firstIncomplete(steps) {
  const index = steps.findIndex(step => !step.valid);
  return index < 0 ? Math.max(0, steps.length - 1) : index;
}

export default function OrgasmeatMenu({ menu, sections = [], selections = {}, onSelect, onRecipeChange, onClose, onConfirm }) {
  const summary = useMemo(() => summarize(menu, sections, selections), [menu, sections, selections]);
  const [stepIndex, setStepIndex] = useState(() => firstIncomplete(summary.steps));
  const menuId = useRef(menu?.id);
  const dialog = useRef(null);
  const heading = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  const step = summary.steps[stepIndex];

  useEffect(() => {
    if (menuId.current !== menu?.id) {
      menuId.current = menu?.id;
      setStepIndex(firstIncomplete(summary.steps));
    } else if (stepIndex >= summary.steps.length && summary.steps.length) {
      setStepIndex(summary.steps.length - 1);
    }
  }, [menu?.id, summary.steps, stepIndex]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    heading.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus?.();
    };
  }, []);

  useEffect(() => { heading.current?.focus(); }, [stepIndex]);

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation(); close.current?.(); return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialog.current?.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]') || []);
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (!first) { event.preventDefault(); heading.current?.focus(); return; }
    if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
      event.preventDefault(); first.focus();
    }
  };

  return <div className="kiosk-modal-backdrop orgasmeat-menu-backdrop" onClick={onClose}>
    <div ref={dialog} className="kiosk-modal orgasmeat-menu" role="dialog" aria-modal="true" aria-label="Composer mon menu" onClick={event => event.stopPropagation()} onKeyDown={handleKeyDown}>
      <header className="orgasmeat-menu-header">
        <div><span className="orgasmeat-eyebrow">À ton goût, à chaque étape.</span><h2>Compose ton menu</h2><p>{menu?.name}</p></div>
        <button type="button" className="kiosk-modal-close" onClick={onClose} aria-label="Fermer la composition du menu">×</button>
      </header>
      <nav className="orgasmeat-menu-progress" aria-label="Étapes du menu">
        {summary.steps.map((item, index) => <button key={item.id} type="button" aria-current={stepIndex === index ? 'step' : undefined}
          aria-label={`Étape ${index + 1} : ${item.name}${item.valid ? ', complétée' : ''}`} onClick={() => setStepIndex(index)}>
          <span className="orgasmeat-menu-step-number" aria-hidden="true">{item.valid ? '✓' : index + 1}</span><span>{item.name}</span>
        </button>)}
      </nav>
      <div className="orgasmeat-menu-body">
        <section className="orgasmeat-menu-choices" aria-labelledby="orgasmeat-menu-step-heading">
          <h3 id="orgasmeat-menu-step-heading" ref={heading} tabIndex={-1}>{stepTitle(step)}</h3>
          {step && <p className="orgasmeat-menu-step-hint">{step.valid ? 'Ton choix est prêt. Tu peux continuer ou changer d’envie.' : `${step.selected.length} sur ${step.required} sélectionné${step.selected.length > 1 ? 's' : ''}`}</p>}
          {step?.products.length ? <div className="orgasmeat-menu-grid">
            {step.products.map(product => {
              const quantity = step.selected.filter(item => sameId(item.id, product.id)).length;
              const selected = quantity > 0;
              const extra = surcharge(product);
              const content = <>
                <MenuProductImage product={product} />
                <OrgasmeatEmoji name={product.name} className="orgasmeat-menu-burger-emoji" />
                <span className="orgasmeat-menu-choice-copy"><strong>{getOrgasmeatDisplayName(product.name)}</strong><span>{extra === null ? 'Prix indisponible' : extra === 0 ? 'Inclus' : `${extra > 0 ? '+' : ''}${euro(extra)}`}</span></span>
              </>;
              if (step.required > 1) return <div key={product.id} className={`kiosk-choice-card orgasmeat-menu-quantity-card${selected ? ' is-selected' : ''}`} role="group" aria-label={product.name}>
                {content}
                <div className="orgasmeat-menu-quantity">
                  <button type="button" aria-label={`Retirer ${product.name}`} disabled={!quantity} onClick={() => onSelect(step, product, -1)}>−</button>
                  <output aria-label={`Quantité ${product.name}`} aria-live="polite">{quantity}</output>
                  <button type="button" aria-label={`Ajouter ${product.name}`} disabled={extra === null || step.selected.length >= step.required} onClick={() => onSelect(step, product, 1)}>+</button>
                </div>
              </div>;
              return <button key={product.id} type="button" className={`kiosk-choice-card${selected ? ' is-selected' : ''}`} aria-pressed={selected}
                disabled={extra === null} onClick={() => onSelect(step, product)}>
                {content}{selected && <span className="orgasmeat-menu-choice-tick" aria-hidden="true">✓</span>}
              </button>;
            })}
          </div> : <p className="orgasmeat-menu-empty" role="status">Aucun choix disponible pour cette étape. Tu peux revenir à la carte.</p>}
          {step?.selected.map((product, selectionIndex) => product.stockChoices && <div className="orgasmeat-menu-recipe" key={`${product.id}-${selectionIndex}`} role={step.required > 1 ? 'group' : undefined} aria-label={step.required > 1 ? `${product.name}, portion ${selectionIndex + 1}` : undefined}>
            {step.required > 1 && <h4>{product.name} · portion {selectionIndex + 1}</h4>}
            <StockRecipeChoices product={product} onChange={stockChoice => onRecipeChange(step, product, stockChoice, selectionIndex)} />
          </div>)}
        </section>
        <aside className="orgasmeat-menu-summary" aria-label="Récapitulatif du menu">
          <h3>Ton menu</h3>
          <ol>{summary.steps.map((item, index) => <li key={item.id}>
            <button type="button" onClick={() => setStepIndex(index)} aria-label={`Modifier ${item.name}`}><span>{item.name}</span><strong>{item.selected.length ? item.selected.map(product => product.name).join(' · ') : 'À choisir'}</strong></button>
            {item.selected.length > 0 && !item.valid && <small>À compléter</small>}
          </li>)}</ol>
          {menu?.description && <p>{menu.description}</p>}
        </aside>
      </div>
      <footer className="orgasmeat-menu-footer">
        <output className="orgasmeat-menu-total" aria-label="Total du menu" aria-live="polite" aria-atomic="true"><span>{summary.complete ? 'Total du menu' : 'Dès'}</span><strong>{summary.total === null ? 'Indisponible' : euro(summary.total)}</strong></output>
        <div className="orgasmeat-menu-actions">
          <button type="button" className="kiosk-cta secondary" disabled={stepIndex === 0} onClick={() => setStepIndex(index => Math.max(0, index - 1))}>Précédent</button>
          {stepIndex < summary.steps.length - 1
            ? <button type="button" className="kiosk-cta" disabled={!step?.valid} onClick={() => setStepIndex(index => index + 1)}>Continuer</button>
            : <button type="button" className="kiosk-cta" disabled={!summary.complete} onClick={() => { if (summary.complete) onConfirm(); }}>Ajouter ce menu <OrgasmeatActionIcon name="basket" /></button>}
        </div>
      </footer>
    </div>
  </div>;
}
